import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import {
  BsEnvelope,
  BsFileEarmarkPdf,
  BsGithub,
  BsLinkedin,
} from "react-icons/bs";
import { devicesMax } from "../config/devices";
import { PORTFOLIO_CONTACT, PORTFOLIO_PROFILE } from "../config/portfolio-content";
import { NavigationData } from "../config/data-navs";
import { RESUME_ENABLED, RESUME_PDF_PATH, RESUME_PDF_TITLE } from "../config/resume";
import BrandLogo from "./brand-logo";
import ResumePreviewModal from "./resume-preview-modal";

const EMAIL = PORTFOLIO_CONTACT.email;
const MAILTO = `mailto:${EMAIL}?subject=${encodeURIComponent(
  "Portfolio inquiry"
)}&body=${encodeURIComponent("Hi Mark,\n\n")}`;

const YEAR = new Date().getFullYear();

type FooterLinkItem =
  | {
      icon: ReactNode;
      href: string;
      label: string;
      external?: boolean;
    }
  | {
      icon: ReactNode;
      label: string;
      preview: true;
    };

const SOCIAL_LINKS: FooterLinkItem[] = [
  {
    icon: <BsGithub />,
    href: PORTFOLIO_CONTACT.github,
    label: "GitHub profile",
    external: true,
  },
  {
    icon: <BsLinkedin />,
    href: PORTFOLIO_CONTACT.linkedin,
    label: "LinkedIn profile",
    external: true,
  },
  {
    icon: <BsEnvelope />,
    href: MAILTO,
    label: `Send email to ${EMAIL}`,
  },
  {
    icon: <BsFileEarmarkPdf />,
    label: "View resume (PDF)",
    preview: true,
  },
];

type FooterProps = {
  onOpenContact: () => void;
};

const Footer = ({ onOpenContact }: FooterProps) => {
  const [resumePreviewOpen, setResumePreviewOpen] = useState(false);

  return (
    <PageFooter id="footer" role="contentinfo">
      <FooterContainer>
        <FooterGrid>
          <FooterBrand>
            <BrandRow>
              <BrandLogo size={36} />
              <BrandName>Mark Jones Solano</BrandName>
            </BrandRow>
            <FooterTagline>{PORTFOLIO_PROFILE.pitch}</FooterTagline>
            <SocialLinks aria-label="Contact and profiles">
              {SOCIAL_LINKS.filter((link) => RESUME_ENABLED || !("preview" in link)).map(
                (link) =>
                  "preview" in link ? (
                    <SocialButton
                      key={link.label}
                      type="button"
                      aria-label={link.label}
                      title={link.label}
                      onClick={() => setResumePreviewOpen(true)}
                    >
                      {link.icon}
                    </SocialButton>
                  ) : (
                    <SocialLink
                      key={link.label}
                      href={link.href}
                      aria-label={link.label}
                      title={link.label}
                      {...(link.external
                        ? { target: "_blank", rel: "noopener noreferrer" }
                        : {})}
                    >
                      {link.icon}
                    </SocialLink>
                  )
              )}
            </SocialLinks>
          </FooterBrand>

          <FooterLinks>
            <h3>Pages</h3>
            <ul>
              <li>
                <Link to="/home">Home</Link>
              </li>
              {NavigationData.map((nav) => (
                <li key={nav.Section}>
                  <Link to={nav.Section}>{nav.Label}</Link>
                </li>
              ))}
            </ul>
          </FooterLinks>

          <FooterLinks>
            <h3>Work</h3>
            <ul>
              <li>
                <Link to="/project">Featured Projects</Link>
              </li>
              <li>
                <Link to="/about#experience">Experience</Link>
              </li>
              {RESUME_ENABLED && (
                <li>
                  <button type="button" onClick={() => setResumePreviewOpen(true)}>
                    Resume
                  </button>
                </li>
              )}
            </ul>
          </FooterLinks>

          <FooterLinks>
            <h3>Contact</h3>
            <ul>
              <li>
                <button type="button" onClick={onOpenContact}>
                  Send a message
                </button>
              </li>
              <li>
                <a href={MAILTO}>{EMAIL}</a>
              </li>
              <li>
                <a
                  href={PORTFOLIO_CONTACT.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  LinkedIn
                </a>
              </li>
              <li>
                <a
                  href={PORTFOLIO_CONTACT.github}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  GitHub
                </a>
              </li>
            </ul>
          </FooterLinks>
        </FooterGrid>

        <FooterBottom>
          <p>© {YEAR} Mark Jones Solano. All rights reserved.</p>
          <FooterMeta>
            <span>{PORTFOLIO_PROFILE.title}</span>
            <span>·</span>
            <span>{PORTFOLIO_PROFILE.location}</span>
          </FooterMeta>
        </FooterBottom>
      </FooterContainer>

      {RESUME_ENABLED && resumePreviewOpen && (
        <ResumePreviewModal
          src={RESUME_PDF_PATH}
          title={RESUME_PDF_TITLE}
          onClose={() => setResumePreviewOpen(false)}
        />
      )}

    </PageFooter>
  );
};

export default Footer;

const PageFooter = styled.footer`
  padding: var(--space-4xl) 0 var(--space-xl);
  border-top: 1px solid var(--border-color);
  background: var(--bg-primary);
  margin-top: auto;
`;

const FooterContainer = styled.div`
  width: 100%;
  max-width: var(--container-max);
  margin: 0 auto;
  padding-inline: var(--space-lg);
`;

const FooterGrid = styled.div`
  display: grid;
  gap: var(--space-2xl);
  grid-template-columns: 1fr;
  margin-bottom: var(--space-3xl);

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 992px) {
    grid-template-columns: 2fr repeat(3, 1fr);
  }
`;

const FooterBrand = styled.div``;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  margin-bottom: var(--space-md);
`;

const BrandName = styled.span`
  font-size: 0.9375rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const FooterTagline = styled.p`
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0 0 var(--space-lg);
  max-width: 320px;
  line-height: 1.6;
`;

const SocialLinks = styled.nav`
  display: flex;
  gap: var(--space-md);
`;

const socialControlStyles = `
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  color: var(--text-secondary);
  font-size: 1.05rem;
  text-decoration: none;
  transition: all var(--transition);
  background: transparent;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: var(--green);
    border-color: var(--border-accent);
    background: var(--accent-bg-hover);
  }

  &:focus-visible {
    outline: 2px solid var(--green);
    outline-offset: 2px;
  }
`;

const SocialLink = styled.a`
  ${socialControlStyles}
`;

const SocialButton = styled.button`
  ${socialControlStyles}
`;

const FooterLinks = styled.div`
  h3 {
    font-size: 0.8125rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-muted);
    margin: 0 0 var(--space-md);
  }

  ul {
    list-style: none;
    margin: 0;
    padding: 0;
  }

  li {
    margin-bottom: var(--space-sm);
  }

  a,
  button {
    font-size: 0.875rem;
    color: var(--text-secondary);
    text-decoration: none;
    transition: color var(--transition);
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font-family: inherit;

    &:hover {
      color: var(--text-primary);
    }
  }
`;

const FooterBottom = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  padding-top: var(--space-xl);
  border-top: 1px solid var(--border-color);

  @media (min-width: 640px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  p {
    font-size: 0.8125rem;
    color: var(--text-muted);
    margin: 0;
  }
`;

const FooterMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.5rem;
  font-size: 0.8125rem;
  color: var(--text-muted);

  @media ${devicesMax.tablet} {
    justify-content: flex-start;
  }
`;
